import { useCallback, useEffect, useMemo } from 'preact/hooks';
import { useApplicationSetup } from 'services/use-application-setup';
import { useConnect } from 'helpers/use-connect-to-global-store';
import { sessionStore } from 'global/session.store';
import { headerStore } from 'global/header.store';
import { stateStore } from 'global/state.store';
import { FocusableTrapElement, useFocusableElement, useTranslationBase } from '@uw/common-components';
import { includes } from 'helpers/index';
import { layoutStore } from 'global/layout.store';
import { INITIAL_PAGE_STATE, REPORT_PAGE_STATE } from '../../constants/constants';
import { PARTNERS } from './generic-partner-logo';
import { SHORTPOINT } from './shortpoint-logo';
import { PORT_CITY_MARKETING } from './portcitymarketing-logo';

const UWAW_NO_FOOTER = 'uwaw-no-footer';

interface UseFooterProps {
  context: ReturnType<typeof useApplicationSetup>,
  translate: ReturnType<typeof useTranslationBase>
}

export const useFooter = ({
                            context,
                            translate,
                          }: UseFooterProps) => {
  const {
    config,
    tunings,
    accessibilityStatement,
    whiteLabelOptions,
    noManage,
    customBrandingOptions,
  } = context;
  const [isLogged] = useConnect(sessionStore.isLogged$);
  const [isMainMenuUrl] = useConnect(headerStore.isMainMenu);
  const [backState] = useConnect(headerStore.backState);
  const [state] = useConnect(stateStore.state);
  const callbackRef = useFocusableElement(FocusableTrapElement.LastElement);

  const goReport = () => {
    stateStore.state.next(REPORT_PAGE_STATE);
  };

  const goLogin = () => {
    stateStore.state.next('main.login');
  };

  const isFooterNavLinksVisible = useMemo(
    () => !backState || state === 'main.logged.payment.whiteLabelDetails',
    [backState, state],
  );

  const accessibilityStatementVisible = useMemo(() => accessibilityStatement?.enabled, [
    accessibilityStatement?.enabled,
  ]);

  const accessibilityStatementLink = useMemo(() => {
    if (accessibilityStatement?.link && !accessibilityStatement?.link.match('^http')) {
      return `http://${accessibilityStatement.link}`;
    }
    return accessibilityStatement?.link;
  }, [
    accessibilityStatement?.link,
  ]);

  const reportProblemVisible = useMemo(() => !whiteLabelOptions?.wlHideReport, [
    whiteLabelOptions?.wlHideReport,
  ]);

  const manageLinkVisible = useMemo(() => !noManage && !whiteLabelOptions?.wlHideManage, [
    noManage,
    whiteLabelOptions?.wlHideManage,
  ]);

  const isPartnerDefaultLogoEnvironment = !!config.current?.services?.partner && [SHORTPOINT, PORT_CITY_MARKETING].includes(config.current?.services?.partner)
    && !whiteLabelOptions?.wlHideLogo
    && (!customBrandingOptions || !customBrandingOptions.custom_logo_image_path);

  const isGenericPartnerCustomLogoEnvironment = !!config.current?.services?.partner && PARTNERS.includes(config.current?.services?.partner)
    && !!customBrandingOptions && !!customBrandingOptions.custom_logo_image_path

  const asteriskVisible = useMemo(
    () =>
      whiteLabelOptions?.wlHideManage &&
      !whiteLabelOptions?.wlHideAsterisk &&
      includes(['main.home', 'main.logged.payment.whiteLabelDetails'], state),
    [whiteLabelOptions?.wlHideManage, whiteLabelOptions?.wlHideAsterisk, state],
  );

  const isUserwayLogoHidden = useMemo(() => config.current?.services?.WHITE_LABEL?.hide_logo, [
    config,
  ]);

  const accessibilityStatementText = useMemo(() => {
    if (!accessibilityStatement) return '';

    return accessibilityStatement.text === 'widget.footer.statement.label'
      ? translate(accessibilityStatement.text, { site: tunings.site_name })
      : accessibilityStatement.text;
  }, [accessibilityStatement?.text, translate]);

  const clickToMainMenu = useCallback(
    ($event) => {
      $event.stopPropagation();
      const url = isLogged ? 'main.logged.dashboard' : INITIAL_PAGE_STATE;
      stateStore.state.next(url);
    },
    [isLogged],
  );

  const clickBack = useCallback(() => {
    stateStore.state.next(backState);
  }, [backState]);

  useEffect(() => {
      const isEmpty = !backState &&
        whiteLabelOptions?.wlHideReport &&
        whiteLabelOptions?.wlHideManage &&
        whiteLabelOptions?.wlHideLogo &&
        whiteLabelOptions?.wlHideAsterisk &&
        !customBrandingOptions?.custom_logo_image_path &&
        !accessibilityStatement?.enabled;

      isEmpty
        ? layoutStore.mainContainer.addClass(UWAW_NO_FOOTER)
        : layoutStore.mainContainer.removeClass(UWAW_NO_FOOTER);
    },
    [
      backState,
      whiteLabelOptions,
      customBrandingOptions?.custom_logo_image_path,
      accessibilityStatement?.enabled,
    ]);

  return {
    clickToMainMenu,
    clickBack,
    accessibilityStatementText,
    asteriskVisible,
    isPartnerDefaultLogoEnvironment,
    isGenericPartnerCustomLogoEnvironment,
    manageLinkVisible,
    reportProblemVisible,
    accessibilityStatementVisible,
    isFooterNavLinksVisible,
    isUserwayLogoHidden,
    goLogin,
    goReport,
    callbackRef,
    isMainMenuUrl,
    isLogged,
    backState,
    accessibilityStatementLink
  };
};
